// AQI calculation utilities based on EPA guidelines
// https://www.airnow.gov/sites/default/files/2020-05/aqi-technical-assistance-document-sept2018.pdf

/**
 * Converts PM2.5 concentration to AQI value
 * @param {number} pm25 - PM2.5 concentration in μg/m³
 * @returns {number|string} AQI value or "-" for invalid inputs
 */
export const pmToAqi = (pm25) => {
  if (typeof pm25 !== 'number' || isNaN(pm25) || pm25 < 0 || pm25 > 1000) {
    return "-";
  }

  // AQI breakpoints and their corresponding PM2.5 ranges
  const breakpoints = [
    { aqi: [0, 50], pm25: [0, 12.0] },          // Good
    { aqi: [51, 100], pm25: [12.1, 35.4] },     // Moderate
    { aqi: [101, 150], pm25: [35.5, 55.4] },    // Unhealthy for Sensitive Groups
    { aqi: [151, 200], pm25: [55.5, 150.4] },   // Unhealthy
    { aqi: [201, 300], pm25: [150.5, 250.4] },  // Very Unhealthy
    { aqi: [301, 400], pm25: [250.5, 350.4] },  // Hazardous
    { aqi: [401, 500], pm25: [350.5, 500.4] }   // Hazardous
  ];

  // Find the appropriate breakpoint range
  let range = breakpoints.find(({ pm25: [low, high] }) =>
    pm25 >= low && pm25 <= high
  );

  if (!range) {
    if (pm25 > 500.4) {
      // For values above 500, use the last range
      range = breakpoints[breakpoints.length - 1];
    } else {
      return "-";
    }
  }

  // Calculate AQI using the formula:
  // AQI = ((I_high - I_low) / (C_high - C_low)) * (C - C_low) + I_low
  const [aqiLow, aqiHigh] = range.aqi;
  const [pmLow, pmHigh] = range.pm25;

  return Math.round(
    ((aqiHigh - aqiLow) / (pmHigh - pmLow)) * (pm25 - pmLow) + aqiLow
  );
};

/**
 * Gets the air quality category and color based on AQI value
 * @param {number} aqi - Air Quality Index value
 * @returns {Object} Object containing category text and color information
 */
export const getAqiCategory = (aqi) => {
  if (aqi === "-" || isNaN(aqi)) {
    return {
      category: "No Data",
      color: "gray",
      textColor: "text-gray-500",
      bgColor: "bg-gray-100",
      borderColor: "border-gray-500"
    };
  }

  if (aqi <= 50) {
    return {
      category: "Good",
      color: "#10B981",
      textColor: "text-green-500",
      bgColor: "bg-green-100",
      borderColor: "border-green-500"
    };
  }
  if (aqi <= 100) {
    return {
      category: "Moderate",
      color: "#F59E0B",
      textColor: "text-yellow-500",
      bgColor: "bg-yellow-100",
      borderColor: "border-yellow-500"
    };
  }
  if (aqi <= 150) {
    return {
      category: "Unhealthy for Sensitive Groups",
      color: "#F97316",
      textColor: "text-orange-500",
      bgColor: "bg-orange-100",
      borderColor: "border-orange-500"
    };
  }
  if (aqi <= 200) {
    return {
      category: "Unhealthy",
      color: "#EF4444",
      textColor: "text-red-500",
      bgColor: "bg-red-100",
      borderColor: "border-red-500"
    };
  }
  if (aqi <= 300) {
    return {
      category: "Very Unhealthy",
      color: "#7C3AED",
      textColor: "text-purple-500",
      bgColor: "bg-purple-100",
      borderColor: "border-purple-500"
    };
  }
  return {
    category: "Hazardous",
    color: "#991B1B",
    textColor: "text-red-900",
    bgColor: "bg-red-200",
    borderColor: "border-red-900"
  };
};

/**
 * Converts raw PM2.5 readings to AQI and provides category information
 * @param {number} pm25 - PM2.5 concentration in μg/m³
 * @returns {Object} Object containing AQI value and category information
 */
export const getAirQualityInfo = (pm25) => {
  const aqi = pmToAqi(pm25);
  return {
    aqi,
    ...getAqiCategory(aqi)
  };
};